import React, { useState } from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { X, Check, AlertCircle, Crown } from "lucide-react";
import { List, Tag, Task } from "../../types";
import { ProposedTodo } from "../../hooks/useVoiceToTodo";
import { isOverTaskLimit } from "../../lib/saveReviewedTodos";
import Tooltip from "../ui/Tooltip";
import { TodoCard } from "../voice/TodoCard";
import TaskPickerDialog from "./TaskPickerDialog";

export interface ProposedTodoReviewDialogProps {
  ariaLabel: string;
  title: string;
  /** Shown under the title; receives the live draft count. */
  countLabel?: (count: number) => string;
  icon?: React.ReactNode;
  sourceLabel?: string;
  sourceText?: string;
  initialTodos: ProposedTodo[];
  lists: List[];
  tags: Tag[];
  /** When set, each card can become a sub-task of one of these tasks. */
  tasks?: Task[];
  onClose: () => void;
  onAccept: (todos: ProposedTodo[]) => void | Promise<void>;
  acceptLabel?: (count: number) => string;
  emptyTitle?: string;
  emptyHint?: string;
  maxTasks?: number;
  onUpgradeClick?: () => void;
  /** When false, each card's details stay open (capture / email). */
  collapsible?: boolean;
  /** When false, cards have no list picker (email sub-tasks). */
  showList?: boolean;
}

/**
 * Shared review dialog for a list of draft todos — voice, capture, and
 * email-to-subtask all feed the same {@link TodoCard} editor so field changes
 * land in one place.
 */
const ProposedTodoReviewDialog: React.FC<ProposedTodoReviewDialogProps> = ({
  ariaLabel,
  title,
  countLabel,
  icon,
  sourceLabel,
  sourceText,
  initialTodos,
  lists,
  tags,
  tasks,
  onClose,
  onAccept,
  acceptLabel = (n) => `Add ${n} Task${n !== 1 ? "s" : ""}`,
  emptyTitle = "No tasks to add",
  emptyHint = "All tasks have been removed",
  maxTasks,
  onUpgradeClick,
  collapsible = true,
  showList = true,
}) => {
  const [todos, setTodos] = useState<ProposedTodo[]>(initialTodos);
  const [submitting, setSubmitting] = useState(false);
  const [pickingIndex, setPickingIndex] = useState<number | null>(null);

  useEscapeKey(onClose, { enabled: !submitting });

  const handleUpdateTodo = (index: number, updatedTodo: ProposedTodo) => {
    const newTodos = [...todos];
    newTodos[index] = updatedTodo;
    setTodos(newTodos);
  };

  const handleRemoveTodo = (index: number) => {
    setTodos(todos.filter((_, i) => i !== index));
  };

  const isOverLimit = isOverTaskLimit(todos, maxTasks);
  const taskCount = todos.filter((todo) => !todo.parent_task_id).length;

  const handleAcceptAll = async () => {
    if (todos.length === 0 || isOverLimit || submitting) return;
    setSubmitting(true);
    try {
      await onAccept(todos);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-2 sm:p-4 animate-dialog-backdrop">
      <div
        role="dialog"
        aria-label={ariaLabel}
        className="bg-warm-bg-card rounded-xl border border-warm-border w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col animate-dialog-panel"
      >
        <div className="flex items-center justify-between gap-2 p-4 sm:p-6 border-b border-warm-border">
          <div className="flex items-center gap-3 min-w-0">
            {icon && (
              <div className="p-2 bg-warm-blue-light/20 dark:bg-warm-blue/20 rounded-lg flex-shrink-0">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-warm-text-primary truncate">
                {title}
              </h3>
              {countLabel && (
                <p className="text-xs sm:text-sm text-warm-text-tertiary">
                  {countLabel(todos.length)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close review dialog"
            disabled={submitting}
            className="text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1 -m-1 flex-shrink-0 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {sourceLabel && sourceText !== undefined && (
            <div className="px-1">
              <span className="text-xs font-medium text-warm-text-tertiary uppercase tracking-wider">
                {sourceLabel}
              </span>
              <p className="mt-1 text-sm text-warm-text-secondary italic">
                "{sourceText}"
              </p>
            </div>
          )}

          {isOverLimit && (
            <div
              role="alert"
              className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 flex items-start gap-3"
            >
              <AlertCircle
                size={18}
                className="text-amber-500 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 text-sm text-amber-800 dark:text-amber-200">
                <p>
                  You can add up to{" "}
                  <strong>
                    {maxTasks} more task{maxTasks !== 1 ? "s" : ""}
                  </strong>
                  . Remove {Math.max(taskCount - maxTasks!, 0)} to continue, or{" "}
                  {onUpgradeClick ? (
                    <button
                      onClick={onUpgradeClick}
                      className="inline-flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-300 underline hover:no-underline"
                    >
                      <Crown size={14} />
                      upgrade to Pro
                    </button>
                  ) : (
                    "upgrade to Pro"
                  )}{" "}
                  for unlimited tasks.
                </p>
              </div>
            </div>
          )}

          {todos.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle
                size={48}
                className="mx-auto text-warm-border mb-3"
              />
              <p className="text-warm-text-tertiary">{emptyTitle}</p>
              <p className="text-sm text-warm-text-tertiary mt-1">{emptyHint}</p>
            </div>
          ) : (
            <div>
              {todos.map((todo, index) => (
                <div key={index}>
                  {index > 0 && (
                    <div
                      className="my-5 h-px bg-warm-border"
                      role="separator"
                    />
                  )}
                <TodoCard
                  key={index}
                  todo={todo}
                  lists={lists}
                  tags={tags}
                  collapsible={collapsible}
                  showList={showList}
                  attachableTasks={tasks}
                  onChooseParent={
                    tasks ? () => setPickingIndex(index) : undefined
                  }
                  onUpdate={(updated) => handleUpdateTodo(index, updated)}
                  onRemove={() => handleRemoveTodo(index)}
                />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 sm:gap-3 p-4 sm:p-6 border-t border-warm-border">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <Tooltip
            content={
              isOverLimit
                ? `You can only add ${maxTasks} more task${maxTasks !== 1 ? "s" : ""}`
                : ""
            }
            disabled={!isOverLimit}
            multiline
          >
            <button
              onClick={() => void handleAcceptAll()}
              disabled={todos.length === 0 || isOverLimit || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark disabled:bg-warm-border text-white rounded-lg text-sm transition-colors disabled:cursor-not-allowed"
            >
              <Check size={16} />
              {submitting ? "Adding…" : acceptLabel(todos.length)}
            </button>
          </Tooltip>
        </div>
      </div>
      {pickingIndex !== null && tasks && (
        <TaskPickerDialog
          tasks={tasks.filter((task) => !task.archived)}
          lists={lists}
          description="This card will become a sub-task of the task you pick."
          onClose={() => setPickingIndex(null)}
          onSelect={(selection) => {
            const index = pickingIndex;
            setPickingIndex(null);
            setTodos((current) =>
              current.map((item, i) =>
                i === index
                  ? { ...item, parent_task_id: selection.task.id }
                  : item,
              ),
            );
          }}
        />
      )}
    </div>
  );
};

export default ProposedTodoReviewDialog;
